import React, { useState, useRef, useEffect } from "react";
import { Product } from "../../../Interfaces/Product";
import {
  InventoryStockUpdate,
  INVENTORY_UPDATE_REASON_OPTIONS,
  InventoryUpdateReason,
} from "../../../Interfaces/Warehouse";
import "./UpdateInventoryStockInput.css";
import {
  validateInput,
  submitOrder,
} from "./UpdateInventoryStockHelperFunctions";
import { getCurrentlyLoggedInUser } from "../../../CommonFunctions";
import { LoggedInAccount } from "../../../Interfaces/AccountUsers";

interface UpdateInventoryStockInputProps {
  products: Product[];
  updateInventoryStock: InventoryStockUpdate[];
  loggedInAccount: LoggedInAccount | null;
  setUpdateInventoryStock: React.Dispatch<
    React.SetStateAction<InventoryStockUpdate[]>
  >;
}

const UpdateInventoryStockInput: React.FC<UpdateInventoryStockInputProps> = ({
  products,
  updateInventoryStock,
  loggedInAccount,
  setUpdateInventoryStock,
}) => {
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [searchSKU, setSearchSKU] = useState("");
  const [quantity, setQuantity] = useState<string>("");
  const [reasoning, setReasoning] = useState<InventoryUpdateReason>("RESTOCK");
  const [verboseReasoning, setVerboseReasoning] = useState<string>("");
  const [quantityError, setQuantityError] = useState<string>("");
  const [verboseReasoningError, setVerboseReasoningError] =
    useState<string>("");
  const [highlightedProductIndex, setHighlightedProductIndex] = useState<
    number | null
  >(null);
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const skuInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [successfulSubmission, setSuccessfulSubmission] =
    useState<boolean>(false);

  useEffect(() => {
    if (successfulSubmission) {
      alert("Inventory update submitted successfully!");
      setUpdateInventoryStock([]);
      setSuccessfulSubmission(false);
    }
  }, [successfulSubmission, updateInventoryStock, setUpdateInventoryStock]);

  const handleSKUChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const skuSearch = event.target.value;
    setSearchSKU(skuSearch);

    const matches = skuSearch
      ? products.filter((product) =>
          product.sku.toString().startsWith(skuSearch)
        )
      : [];

    setFilteredProducts(matches);
    setHighlightedProductIndex(null);
  };

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Allow empty string, or positive/negative integers (not zero)
    if (value === "" || /^-?[1-9]\d*$/.test(value)) {
      setQuantity(value);
    }
  };

  const handleQuantityKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter" && searchSKU && quantity !== "") {
      const validInput = handleValidate();
      if (!validInput) return;
      const selectedSKU = searchSKU.split(" | ")[0];
      setUpdateInventoryStock((prev) => {
        const existingIndex = prev.findIndex(
          (item) =>
            item.product === Number(selectedSKU) &&
            item.reason === reasoning &&
            item.reason_detail === verboseReasoning
        );
        if (existingIndex > -1) {
          // If SKU already exists, update the quantity
          const updatedList = prev.map((item, index) =>
            index === existingIndex
              ? { ...item, increment: item.increment + Number(quantity) }
              : item
          );
          return updatedList;
        } else {
          // If SKU doesn't exist, add a new entry
          return [
            ...prev,
            {
              product: Number(selectedSKU),
              increment: Number(quantity),
              reason: reasoning,
              reason_detail: verboseReasoning,
              account: loggedInAccount ? loggedInAccount.id : 1,
            },
          ];
        }
      });
      setSearchSKU("");
      setQuantity("");
      setReasoning("RESTOCK");
      setVerboseReasoning("");
      setFilteredProducts([]);

      // Focus back to the SKU input
      skuInputRef.current?.focus();
    }
  };

  const handleProductKeyDown = (event: React.KeyboardEvent) => {
    if (filteredProducts.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedProductIndex((prev) =>
          prev === null ? 0 : Math.min(prev + 1, filteredProducts.length - 1)
        );
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedProductIndex((prev) =>
          prev === null ? filteredProducts.length - 1 : Math.max(prev - 1, 0)
        );
      } else if (event.key === "Enter" && highlightedProductIndex !== null) {
        handleProductSelect(filteredProducts[highlightedProductIndex]);
      }
    }
  };

  const handleValidate = () => {
    setQuantityError("");
    setVerboseReasoningError("");
    console.log("🔍 Validating input:");

    const isValid = validateInput({
      quantity,
      reasoning,
      verboseReasoning,
      setQuantityError,
      setVerboseReasoningError,
    });

    if (isValid) {
      console.log("✅ Validation passed");
      return true;
    }
    return false;
  };

  const handleSubmit = async () => {
    if (!updateInventoryStock.length) {
      alert("Please add at least one product to the order.");
      return;
    }

    await submitOrder({
      orderToSubmit: updateInventoryStock,
      setLoading,
      setSuccessfulSubmission,
    });
  };

  const handleProductSelect = (product: Product) => {
    setSearchSKU(`${product.sku} | ${product.item_display_name}`);
    setFilteredProducts([]);
    setHighlightedProductIndex(null);
    setQuantity("");
    quantityInputRef.current?.focus();
  };
  return (
    <div className="update-inventory-input-container">
      {/* Row 1: SKU / Quantity / Reason */}
      <div className="update-inventory-row">
        <div className="sku-input-container">
          <label htmlFor="sku-search">SKU:</label>
          <input
            id="sku-search"
            type="text"
            value={searchSKU}
            onChange={handleSKUChange}
            onKeyDown={handleProductKeyDown}
            placeholder="Type SKU..."
            autoComplete="off"
            ref={skuInputRef}
          />
          {filteredProducts.length > 0 && (
            <ul className="update-inventory-dropdown">
              {filteredProducts.map((product, index) => (
                <li
                  key={product.sku}
                  onClick={() => handleProductSelect(product)}
                  className={`update-inventory-dropdown-item ${
                    highlightedProductIndex === index
                      ? "update-inventory-highlighted"
                      : ""
                  }`}
                >
                  {product.sku} | {product.item_display_name}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="quantity-input-container">
          <label htmlFor="quantity">Quantity:</label>
          <input
            id="quantity"
            type="number"
            ref={quantityInputRef}
            value={quantity}
            onChange={handleQuantityChange}
            onKeyDown={handleQuantityKeyDown}
            placeholder="Enter quantity"
          />
          {quantityError && <p className="error-message">{quantityError}</p>}
        </div>

        <div className="reasoning-input-container">
          <label htmlFor="reasoning-input">Reason:</label>
          <select
            id="reasoning-input"
            value={reasoning}
            onChange={(e) =>
              setReasoning(e.target.value as InventoryUpdateReason)
            }
          >
            {Object.values(INVENTORY_UPDATE_REASON_OPTIONS).map((reason) => (
              <option key={reason.label} value={reason.value}>
                {reason.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Row 2: Verbose Reasoning */}
      <div className="update-inventory-text-field-row">
        <div className="update-inventory-text-field-input">
          <label htmlFor="verbose-reasoning">Detailed Reason:</label>
          <textarea
            id="verbose-reasoning"
            value={verboseReasoning}
            onChange={(e) => setVerboseReasoning(e.target.value)}
            placeholder="Provide more details..."
          />
          {verboseReasoningError && (
            <p className="error-message">{verboseReasoningError}</p>
          )}
        </div>
      </div>

      {/* Row 3: Submit Button */}
      <div className="update-inventory-submit-row">
        <button
          onClick={handleSubmit}
          disabled={updateInventoryStock.length === 0 || loading}
        >
          {loading ? "Submitting..." : "Submit Order"}
        </button>
      </div>
    </div>
  );
};
export default UpdateInventoryStockInput;
